// @flow

import React, {useState, useCallback, useEffect, useRef} from 'react';
import {cutTextPerLines} from '@pb/kit-component-multiline-ellipsis';
import {hashedClasses} from '@pb/kit-util-with-css-module';
import MoreLessButton from '../more-less-button';
import styles from './more-less-text.component.module.styl';

import type {ComponentType, Element} from 'react';

const classes = hashedClasses(styles);

/**
 * @component
 * @name MoreLessText
 * @file
 Use a component to hide part of the data text.
 The component has the ability to show or hide the words of the end part of the text.
 */

const PERCENTAGE = 25;

/**
 * MoreLessText Props
 */
type Props = {|
    /**
     * Data items
     */
    data?: string,
    /**
     * Set component to show button arrows state
     */
    showArrows?: boolean,
    /**
     * Number of text words to be shown initially
     */
    count?: number,
    /**
     * Call when click more/less button
     */
    onChange?: (value: boolean) => void,
    /**
     * Children nodes
     */
    children?: ({ isCollapsed: boolean, onChange: Function, data?: string, isLargeText?: boolean }) => Element<*>,
    /**
     * Text name of "more" button
     */
    moreText?: string,
    /**
     * Text name of "less" button
     */
    lessText?: string,
    /**
     * Number of lines data text to display
     */
    lines?: number,
|};

export const MoreLessText: ComponentType<Props> = (props: Props) => {
    const {
        children,
        lines,
        onChange,
        count = 100,
        showArrows = true,
        moreText = 'more',
        lessText = 'less',
        data: rawData,
    } = props;

    const data = rawData ? rawData.trim() : '';
    const splitData = data ? data.split(' ') : [];
    const isLargeText = splitData.length > count + Math.ceil((count * PERCENTAGE) / 100);

    const [collapsed, setCollapsed] = useState<boolean>(!!lines || isLargeText);
    const [trimmedText, setTrimmedText] = useState('');
    const [isCalculatedBounds, setIsCalculatedBounds] = useState(false);
    const contentContainerRef = useRef<HTMLElement | null>(null);
    const observerRef = useRef<ResizeObserver | null>(null);

    const onToggleText = useCallback(() => {
        if (onChange) {
            onChange(!collapsed);
        }

        setCollapsed(prevState => !prevState);
    }, [collapsed, onChange]);

    useEffect(() => {
        if (contentContainerRef.current && data && lines) {
            const container = contentContainerRef.current;
            const parentContainer = container.parentElement;

            if (!(parentContainer instanceof HTMLElement)) {
                return;
            }

            const observer = new ResizeObserver(() => {
                const {fontSize, fontWeight, letterSpacing} = getComputedStyle(container);
                const containerStyles = {
                    fontWeight,
                    width: parentContainer.offsetWidth,
                    fontSize: parseFloat(fontSize),
                    letterSpacing: !letterSpacing || letterSpacing === 'normal' ? 0 : parseFloat(letterSpacing),
                };
                const {slicedText, isEllipsis} = cutTextPerLines({
                    styles: containerStyles,
                    text: data,
                    lines,
                });

                if (isEllipsis && data.length > slicedText.trim().length) {
                    setTrimmedText(slicedText.trim());
                } else {
                    setTrimmedText('');
                }

                setIsCalculatedBounds(true);
            });

            if (observerRef.current) {
                observerRef.current.disconnect();
            }

            observerRef.current = observer;
            observerRef.current.observe(container);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [data, lines]);

    const getButton = () => {
        if (children) {
            return children({isCollapsed: collapsed, onChange: onToggleText, data});
        }

        return (
            <MoreLessButton
                {...{
                    mix: classes['more-less-text__button'],
                    moreText,
                    lessText,
                    showArrows,
                    isCollapsed: collapsed,
                    onChange: onToggleText,
                }}
            />
        );
    };

    if (typeof data !== 'string') {
        return null;
    }

    const MORE_LINK_SYMBOLS = moreText.length + +showArrows + 7;
    const shortData = splitData.slice(0, count).join(' ');

    if (lines) {
        return (
            <div className={classes['more-less-text']} ref={contentContainerRef}>
                <span>
                    {
                        isCalculatedBounds && (
                            collapsed && trimmedText
                                ? trimmedText.slice(0, trimmedText.length - MORE_LINK_SYMBOLS).trim() + '...'
                                : data
                        )
                    }
                </span>
                {trimmedText ? getButton() : null}
            </div>
        );
    }

    return (
        <p>
            {children && !isLargeText ? (
                children({isCollapsed: collapsed, onChange: onToggleText, data, isLargeText})
            ) : (
                <>
                    {collapsed ? shortData : `${data} `}
                    {isLargeText ? getButton() : null}
                </>
            )}
        </p>
    );
};
